import { ChartBarIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarSolidIcon,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import ConfigurationView from "./ConfigurationView";
import { LoopConfiguration, LoopType } from "./types";
import { useLoop, useLoopInputVariables } from "./hooks";

import { BlockProps, isEvalLikeEnv, useBlockEnv } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import BlockStatistics from "@/components/PowerBlock/Block/BlockStatistics";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import TabNavigation from "@/components/ui/TabNavigation";
import { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";

const validationSchema = createBlockSchema<LoopConfiguration>()
  .requireName()
  .addRule(required("configuration.loop_type", "Loop type is required"))
  .addRule({
    path: "configuration.prompt_config.template.name",
    validate: (_value, fullPayload) => {
      if (fullPayload.configuration?.loop_type === LoopType.PROMPT) {
        // Valid if either registry template or inline template is configured
        const hasRegistryTemplate = !!fullPayload.configuration?.prompt_config?.template?.name;
        const hasInlineTemplate =
          !!fullPayload.configuration?.prompt_config?.inline_template?.prompt_template;
        return hasRegistryTemplate || hasInlineTemplate;
      }
      return true;
    },
    message: "Prompt template selection is required",
  })
  .addRule({
    path: "configuration.workflow_config.name",
    validate: (_value, fullPayload) => {
      if (fullPayload.configuration?.loop_type === LoopType.WORKFLOW) {
        return !!fullPayload.configuration?.workflow_config?.name;
      }
      return true;
    },
    message: "Workflow selection is required",
  })
  .build();

export const LoopBlockCore = observer(
  ({
    showVerboseMode = true,
    sources = createEmptySources(),
    id,
    readonly,
  }: Omit<BlockProps<LoopConfiguration>, "DefaultFields"> & {
    showVerboseMode?: boolean;
  }) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const env = useBlockEnv();
    const isEval = isEvalLikeEnv(env);

    const {
      configuration,
      loopType,
      setLoopType,
      setPromptConfig,
      setWorkflowConfig,
      setEndConditionJsonPath,
      setMaxIterations,
      setVariableMappings,
    } = useLoop();

    const handleTabChange = (tabId: number) => {
      setCurrentTabIndex(tabId);
    };

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        setVariableMappings({
          ...configuration?.variable_mappings,
          ...mappings,
        });
      },
      [configuration?.variable_mappings, setVariableMappings],
    );

    const { inputVariables, isLoading: inputVariablesIsLoading } =
      useLoopInputVariables(configuration);

    const enhancedSources = useMemo(
      () => ({
        ...sources,
        [SourceType.InputVariable]: [
          ...(sources[SourceType.InputVariable] || []),
          "loop_index",
          "previous_outputs",
        ],
      }),
      [sources],
    );

    const configurationContent = (
      <ConfigurationView
        loopType={loopType}
        endConditionJsonPath={configuration?.end_condition_json_path}
        maxIterations={configuration?.max_iterations}
        inputVariables={inputVariables}
        sources={enhancedSources}
        inputVariablesIsLoading={inputVariablesIsLoading}
        readonly={readonly}
        onLoopTypeChange={setLoopType}
        onEndConditionChange={setEndConditionJsonPath}
        onMaxIterationsChange={setMaxIterations}
        onAutofill={handleAutofill}
        promptConfig={configuration?.prompt_config}
        onPromptConfigChange={setPromptConfig}
        showVerboseMode={showVerboseMode}
        workflowConfig={configuration?.workflow_config}
        onWorkflowConfigChange={setWorkflowConfig}
      />
    );

    return (
      <>
        {readonly && isEval ? (
          <TabNavigation
            selectedIndex={currentTabIndex}
            handleTabChange={handleTabChange}
            tabs={[
              {
                id: 0,
                name: "Overview",
                outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                content: <div className="pt-2">{configurationContent}</div>,
              },
              {
                id: 1,
                name: "Statistics",
                outlineIcon: <ChartBarIcon className="h-4 w-4" />,
                solidIcon: <ChartBarSolidIcon className="h-4 w-4" />,
                content: <BlockStatistics id={id as number} />,
              },
            ]}
          />
        ) : (
          configurationContent
        )}
      </>
    );
  },
);

export default withBlockStore<LoopConfiguration>(LoopBlockCore, {
  validationSchema,
  defaultConfiguration: {
    loop_type: LoopType.PROMPT,
    max_iterations: 10,
  } as LoopConfiguration,
});
